import type { ElementRef, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { RtUIOptions } from '../../options';
import { RtInput } from '../../utils/rt-input';

@Component({
  selector: 'rt-range-selection-input',
  templateUrl: './range-selection-input.component.html',
  styleUrls: ['./range-selection-input.component.less'],
  host: {
    '[class.disabled]': 'disabled',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class RangeSelectionInputComponent extends RtInput<[number, number]> {
  @Input() public min: number = 0;
  @Input() public max: number = 100;
  @Input() public step: number = 1;
  @Input() public allowEmptyRange: boolean = true;

  @Output() public startDragging: EventEmitter<void> = new EventEmitter<void>();
  @Output() public endDragging: EventEmitter<void> = new EventEmitter<void>();

  public dragging: 'startHandle' | 'endHandle' | 'filledTrack' | false = false;
  private tmpValue: [number, number] | null = null;
  private mouseDownX: number | null = null;

  @ViewChild('track', { static: true }) private track: ElementRef;
  @ViewChild('filledTrack', { static: true }) private filledTrack: ElementRef;
  @ViewChild('startHandle', { static: true }) private startHandle: ElementRef;
  @ViewChild('endHandle', { static: true }) private endHandle: ElementRef;

  constructor(
    private rtUiOptions: RtUIOptions,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public ngOnInit(): void {
    this.renderValue(this.value!);
    this.setupListeners();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      if (changes.value.previousValue !== changes.value.currentValue) {
        this.renderValue(this.value!);
      }
    }
  }

  protected setValue(value: [number, number]): void {
    this.value = value;
    this.renderValue(value);
  }

  // eslint-disable-next-line max-lines-per-function
  private setupListeners(): void {
    const onMouseMove = (e: MouseEvent | TouchEvent) => {
      switch (this.dragging) {
        case 'startHandle':
          this.handleStartHandleDrag(e);
          break;
        case 'endHandle':
          this.handleEndHandleDrag(e);
          break;
        case 'filledTrack':
          this.handleFilledTrackDrag(e);
          break;
        default:
          return;
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove, false);
      document.removeEventListener('mouseup', onMouseUp, false);
      document.removeEventListener('touchmove', onMouseMove, false);
      document.removeEventListener('touchend', onMouseUp, false);
      this.handleDragFinish();
    };

    const onMouseDown = (e: MouseEvent | TouchEvent) => {
      switch (e.target) {
        case this.startHandle.nativeElement:
          this.dragging = 'startHandle';
          this.startDragging.emit();
          break;
        case this.endHandle.nativeElement:
          this.dragging = 'endHandle';
          this.startDragging.emit();
          break;
        case this.filledTrack.nativeElement:
          this.dragging = 'filledTrack';
          break;
        default:
          if (this.rtUiOptions.debug) {
            console.error('Unknown event.target in RangeSelectionInput.onMouseDown:', e.target);
          }
          return;
      }
      this.mouseDownX = 'touches' in e ? (<TouchEvent>e).touches[0].pageX : (<MouseEvent>e).pageX;
      document.addEventListener('mousemove', onMouseMove, false);
      document.addEventListener('mouseup', onMouseUp, false);
      document.addEventListener('touchmove', onMouseMove, false);
      document.addEventListener('touchend', onMouseUp, false);
      onMouseMove(e);
    };

    this.startHandle.nativeElement.addEventListener('mousedown', onMouseDown);
    this.startHandle.nativeElement.addEventListener('touchstart', onMouseDown);
    this.endHandle.nativeElement.addEventListener('mousedown', onMouseDown);
    this.endHandle.nativeElement.addEventListener('touchstart', onMouseDown);
    this.filledTrack.nativeElement.addEventListener('mousedown', onMouseDown);
    this.filledTrack.nativeElement.addEventListener('touchstart', onMouseDown);
  }

  private handleStartHandleDrag(e: MouseEvent | TouchEvent): void {
    const x = 'touches' in e ? (<TouchEvent>e).touches[0].pageX : (<MouseEvent>e).pageX;
    const rect = this.track.nativeElement.getBoundingClientRect();
    const v = this.normalizeValue(
      ((x - rect.left) / (rect.right - rect.left)) * (this.max - this.min) + this.min,
    );

    if (v <= this.value![1]) {
      this.tmpValue = [v, this.value![1]];
      this.renderValue(this.tmpValue);
      this.handleChange(this.tmpValue);
    }
  }

  private handleEndHandleDrag(e: MouseEvent | TouchEvent): void {
    const x = 'touches' in e ? (<TouchEvent>e).touches[0].pageX : (<MouseEvent>e).pageX;
    const rect = this.track.nativeElement.getBoundingClientRect();
    let v = this.normalizeValue(
      ((x - rect.left) / (rect.right - rect.left)) * (this.max - this.min) + this.min,
    );

    if (!this.allowEmptyRange && v === 0 && this.value![0] === 0) {
      v = this.value![0] + this.step;
    }
    if (v >= this.value![0]) {
      this.tmpValue = [this.value![0], v];
      this.renderValue(this.tmpValue);
      this.handleChange(this.tmpValue);
    }
  }

  private handleFilledTrackDrag(e: MouseEvent | TouchEvent): void {
    const x = 'touches' in e ? (<TouchEvent>e).touches[0].pageX : (<MouseEvent>e).pageX;
    const rect = this.track.nativeElement.getBoundingClientRect();
    let change = ((x - this.mouseDownX!) / (rect.right - rect.left)) * (this.max - this.min);
    change = this.value![0] + change < this.min ? -(this.value![0] - this.min) : change;
    change = this.value![1] + change > this.max ? this.max - this.value![1] : change;
    this.tmpValue = [
      this.normalizeValue(this.value![0] + change),
      this.normalizeValue(this.value![1] + change),
    ];
    this.renderValue(this.tmpValue);
  }

  private handleDragFinish(): void {
    this.endDragging.emit();
    this.dragging = false;
    if (this.tmpValue) {
      this.handleChange(this.tmpValue);
      this.tmpValue = null;
    }
  }

  private renderValue(value: [number, number]): void {
    const start = ((value[0] - this.min) / (this.max - this.min)) * 100;
    const end = ((value[1] - this.min) / (this.max - this.min)) * 100;
    const width = ((value[1] - value[0]) / (this.max - this.min)) * 100;
    this.filledTrack.nativeElement.style.width = `${width}%`;
    this.filledTrack.nativeElement.style.left = `${start}%`;
    this.startHandle.nativeElement.style.left = `${start}%`;
    this.endHandle.nativeElement.style.left = `${end}%`;

    if (start === end && start === 100) {
      this.startHandle.nativeElement.style.zIndex = 4;
    } else {
      this.startHandle.nativeElement.style.zIndex = 3;
    }
    if (start === end && start === 0) {
      this.endHandle.nativeElement.style.zIndex = 4;
    } else {
      this.endHandle.nativeElement.style.zIndex = 3;
    }
  }

  private normalizeValue(v: number): number {
    let value = Math.min(v, this.max);

    value = Math.max(value, this.min);

    return Math.round(value / this.step) * this.step;
  }
}
